/**
 * Per-clip SPEED CURVE — shared by preview + export. A speed curve is a piecewise-linear set of
 * {t (0..1 of clip), rate} points; the effective source time is the INTEGRAL of the rate over
 * elapsed clip time (so a ramp consumes source correctly). Absent curve → the existing linear
 * mapping `(t - clipStart) * playbackRate + sourceStart`. Pure math, no backend.
 */

import { applyEase } from './enter-exit-presets';
import type { KeyframeEase } from './transform-keyframes.util';

export interface SpeedCurve {
  /** Points sorted by t (0..1), each with a rate multiplier (>0). */
  points: { t: number; rate: number; ease?: KeyframeEase }[];
}

/** Effective rate at clip-relative progress p (0..1), piecewise-linear. */
export function speedRateAt(curve: SpeedCurve | null | undefined, p: number, fallbackRate = 1): number {
  const pts = curve?.points;
  if (!pts || pts.length === 0) return fallbackRate;
  const q = p < 0 ? 0 : p > 1 ? 1 : p;
  if (q <= pts[0].t) return Math.max(0.05, pts[0].rate);
  for (let i = 1; i < pts.length; i++) {
    if (q <= pts[i].t) {
      const a = pts[i - 1], b = pts[i];
      const span = b.t - a.t || 1;
      const progress = applyEase((q - a.t) / span, a.ease);
      return Math.max(0.05, a.rate + (b.rate - a.rate) * progress);
    }
  }
  return Math.max(0.05, pts[pts.length - 1].rate);
}

/**
 * Map a timeline time to a SOURCE time. With a curve, integrate rate over elapsed clip time;
 * without one, the classic linear mapping. Result is clamped ≥ 0.
 */
export function speedSourceTime(
  timelineTime: number,
  clipStart: number,
  clipEnd: number,
  sourceStart: number,
  curve: SpeedCurve | null | undefined,
  fallbackRate = 1,
): number {
  if (!curve?.points?.length) {
    return Math.max(0, (timelineTime - clipStart) * fallbackRate + sourceStart);
  }
  const dur = Math.max(0.0001, clipEnd - clipStart);
  const elapsed = Math.max(0, Math.min(dur, timelineTime - clipStart));
  const steps = 24;
  const hstep = elapsed / steps;
  let src = sourceStart;
  for (let i = 0; i < steps; i++) {
    const p = ((i + 0.5) * hstep) / dur;
    src += speedRateAt(curve, p, fallbackRate) * hstep;
  }
  return Math.max(0, src);
}
